import { useState } from 'react';
import { OS_BASE } from '../lib/osData';

interface Item { label: string; path?: string; url?: string; crm?: boolean }
interface SubGroup { title?: string; items: Item[] }
interface Dept { label: string; color: string; ico: string; path?: string; url?: string; groups?: SubGroup[] }

// Mirrors the OS department menu — colors + nested subsections.
const DEPTS: Dept[] = [
  { label: 'Quotes', color: '#22d3ee', ico: '❑', path: 'quotes' },
  { label: 'Kanban Board', color: '#38bdf8', ico: '▦', path: 'dashboard' },
  { label: 'Dashboard', color: '#94a3b8', ico: '◧', path: 'dashboard' },
  { label: 'Job Ticket Tracker', color: '#00e5ff', ico: '▤', path: 'dept-jt-home', groups: [
    { items: [{ label: 'Daily Dash', path: 'jobtracker' }] },
  ] },
  { label: 'Client Services', color: '#a855f7', ico: '☷', path: 'clientservices', groups: [
    { items: [{ label: 'Requests', crm: true, path: 'requests' }, { label: 'Daily Dash', path: 'clientservices' }, { label: 'Open CRM', crm: true }] },
    { title: 'Sales', items: [{ label: 'Clients / Accounts', path: 'customers' }, { label: 'Account Health', path: 'customers' }, { label: 'Deals', path: 'sales' }] },
    { title: 'Estimation', items: [{ label: 'Quotes', path: 'quotes' }, { label: 'Quote Pipeline', path: 'orders' }, { label: 'Purchase Orders', path: 'orders' }, { label: 'Sales Orders', path: 'orders' }] },
    { title: 'CSR / Account Mgmt', items: [{ label: 'Active Orders', path: 'orders' }, { label: 'Job Lifecycle', path: 'jobtracker' }, { label: 'Blueprint Library', path: 'ppd' }] },
  ] },
  { label: 'Pre-Press', color: '#a3e635', ico: '◰', path: 'ppd', groups: [
    { items: [{ label: 'Daily Dash', path: 'ppd' }, { label: 'Blueprint Library', path: 'ppd' }] },
  ] },
  { label: 'Logistics', color: '#fb923c', ico: '⛟', path: 'logistics' },
  { label: 'Production', color: '#818cf8', ico: '⚙', path: 'production' },
  { label: 'Quality', color: '#f87171', ico: '◇', path: 'dept-quality-home' },
  { label: 'Finance+', color: '#4ade80', ico: '$', path: 'dept-finance-home' },
  { label: 'Operations', color: '#cbd5e1', ico: '⊞', path: 'dept-operations-home' },
  { label: 'FSQMS · Food Safety & Quality', color: '#facc15', ico: '✓', path: 'fsqms' },
  { label: 'FlexAi · Intelligence', color: '#22d3ee', ico: '✦', path: 'aiops' },
  { label: 'MicroflexFilm.com', color: '#38bdf8', ico: '◍', url: 'https://microflexfilm.com' },
];

const itemHref = (it: Item) => it.url || (it.crm ? (it.path ? `/${it.path}` : '/') : `${OS_BASE}/${it.path}`);

export default function OsMenu() {
  const [open, setOpen] = useState<Record<string, boolean>>({ 'Client Services': true });

  return (
    <div className="osm">
      <div className="nav-group">Modules</div>
      {DEPTS.map((d) => {
        const tint = (a: string) => d.color + a;
        const style = { background: `linear-gradient(135deg, ${tint('1f')}, ${tint('0d')})`, border: `1px solid ${tint('45')}`, color: d.color };
        if (!d.groups) {
          return (
            <a key={d.label} className="osm-dept" style={style} href={itemHref(d)} target={d.url ? '_blank' : '_blank'} rel="noreferrer">
              <span className="osm-ico" style={{ color: d.color }}>{d.ico}</span>
              <span className="osm-label">{d.label}</span>
              <span className="osm-ext">↗</span>
            </a>
          );
        }
        const isOpen = !!open[d.label];
        return (
          <div key={d.label}>
            <button className="osm-dept" style={style} onClick={() => setOpen((o) => ({ ...o, [d.label]: !o[d.label] }))}>
              <span className="osm-ico" style={{ color: d.color }}>{d.ico}</span>
              <span className="osm-label">{d.label}</span>
              <span className="osm-arr" style={{ transform: isOpen ? 'rotate(90deg)' : 'none' }}>›</span>
            </button>
            {isOpen && (
              <div className="osm-subwrap">
                {d.groups.map((g, gi) => (
                  <div key={gi} className="osm-group">
                    {g.title && <div className="osm-grouptitle" style={{ color: d.color }}>{g.title}</div>}
                    {g.items.map((it) => (
                      <a key={it.label} className="osm-item" href={itemHref(it)} target={it.crm ? undefined : '_blank'} rel="noreferrer">
                        <span className="osm-dot" style={{ background: d.color }} />{it.label}
                      </a>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
